// Organisatie → Ticket instellen (TenantTicketSettings) — FRONTEND-BLUEPRINT §3.7; PLAN-W30. Per ticket
// kind the tenant configures a procedure-template body. The ticket-kind library is owner/counsel-seeded
// and currently empty → the surface shows an honest note (nothing to configure yet). Gated
// tenant_settings.read / .update.
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Ticket } from "lucide-react";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { PageHeader } from "../../components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);

interface TicketSetting { ticketKindId: string; code: string; displayName: string; category: string; settingsId: string | null; procedureTemplateBody: string }

export function TicketInstellenRoute() {
  const { t } = useI18n();
  const { data, isLoading, error } = useQuery({ queryKey: ["ticket-settings"], queryFn: () => api.listTicketSettings() as Promise<TicketSetting[]> });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("tickset.title")} description={t("tickset.sub")} />
      {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
      {error && <p className="text-sm text-destructive">{errText(error)}</p>}
      {data && (data.length
        ? <div className="flex flex-col gap-4">{data.map((s) => <TicketKindCard key={s.ticketKindId} setting={s} />)}</div>
        : <p className="text-sm text-muted-foreground">{t("tickset.empty")}</p>)}
    </div>
  );
}

function TicketKindCard({ setting }: { setting: TicketSetting }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [body, setBody] = useState(setting.procedureTemplateBody ?? "");
  useEffect(() => { setBody(setting.procedureTemplateBody ?? ""); }, [setting.procedureTemplateBody]);
  const save = useMutation({
    mutationFn: () => api.upsertTicketSettings({ ticketKindId: setting.ticketKindId, procedureTemplateBody: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ticket-settings"] }),
  });

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Ticket className="size-4 text-primary" aria-hidden />
          {setting.displayName}
          <Badge variant="secondary">{setting.category}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <label className="text-sm font-medium" htmlFor={`tk-${setting.ticketKindId}`}>{t("tickset.procedure")}</label>
          <Textarea id={`tk-${setting.ticketKindId}`} value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder={t("tickset.placeholder")} />
          <div className="flex items-center gap-3">
            <Can perm="tenant_settings.update" fallback={<p className="text-sm text-muted-foreground">{t("settings.readonly")}</p>}>
              <Button type="submit" disabled={save.isPending}>{save.isPending ? t("crud.saving") : t("common.save")}</Button>
            </Can>
            {save.isSuccess && <span className="text-sm text-emerald-600">{t("tickset.saved")}</span>}
            {save.isError && <span className="text-sm text-destructive">{errText(save.error)}</span>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
